import {put, select, takeEvery} from 'redux-saga/effects';
import {toast} from 'react-toastify';
import {
  getByocOnboardingDataApi,
  saveByocAdjustmentPriceRulesApi,
  getByocSelfDeliveryLinkApi,
  updateByocOnboardingStepApi,
  saveByocSignatureApi
} from '../../helpers/services/api/byocOnboarding';
import {
  GET_BYOC_ONBOARDING_DATA,
  GET_BYOC_ONBOARDING_DATA_SUCCESS,
  GET_BYOC_ONBOARDING_DATA_ERROR,
  SAVE_BYOC_ADJUSTMENT_PRICE_RULES,
  SAVE_BYOC_ADJUSTMENT_PRICE_RULES_SUCCESS,
  SAVE_BYOC_ADJUSTMENT_PRICE_RULES_ERROR,
  GET_BYOC_AGREEMENT,
  GET_BYOC_AGREEMENT_SUCCESS,
  GET_BYOC_AGREEMENT_ERROR,
  SAVE_BYOC_STRIPE_FLOW_COMPLETED,
  SAVE_BYOC_STRIPE_FLOW_COMPLETED_SUCCESS,
  UPDATE_BYOC_ONBOARDING_STEP,
  UPDATE_BYOC_ONBOARDING_STEP_SUCCESS,
  UPDATE_BYOC_ONBOARDING_STEP_ERROR,
  SAVE_BYOC_SIGNATURE,
  SAVE_BYOC_SIGNATURE_SUCCESS,
  SAVE_BYOC_SIGNATURE_ERROR,
  UPDATE_BYOC_COMMISSION_STATE
} from './actions';
import {
  markPaymentDetailsAsFinished,
  updateStripeFlowCompleted
} from '../../helpers/services/api/stripe';

function* getByocOnboardingData() {
  try {
    const locationId = yield select((state) => state.user?.locationId);

    if (!locationId) {
      yield put(GET_BYOC_ONBOARDING_DATA_ERROR('Location ID is required'));
      return;
    }

    const response = yield getByocOnboardingDataApi(locationId);

    if (response?.status === 200) {
      yield put(GET_BYOC_ONBOARDING_DATA_SUCCESS(response.data));
    } else {
      yield put(
        GET_BYOC_ONBOARDING_DATA_ERROR(
          response?.data?.message || 'Failed to fetch onboarding data'
        )
      );
    }
  } catch (error) {
    yield put(
      GET_BYOC_ONBOARDING_DATA_ERROR(
        error?.response?.data?.message || 'Failed to fetch onboarding data'
      )
    );
  }
}

function* saveByocStripeFlowCompleted() {
  try {
    const locationId = yield select((state) => state.user?.locationId);
    const response = yield updateStripeFlowCompleted(locationId, true);
    if (response?.status === 200 || response?.status === 201) {
      yield put(SAVE_BYOC_STRIPE_FLOW_COMPLETED_SUCCESS());
    }
  } catch (e) {
    console.error(e);
  }
}

function* saveByocAdjustmentPriceRules({payload}) {
  try {
    const locationId = yield select((state) => state.user?.locationId);

    if (!locationId) {
      yield put(
        SAVE_BYOC_ADJUSTMENT_PRICE_RULES_ERROR('Location ID is required')
      );
      return;
    }

    // Format data for new API: { adjustmentPriceRules: [...] }
    const requestData = {
      adjustmentPriceRules:
        payload?.adjustmentPriceRules ||
        payload?.AdjustmentPriceRules ||
        payload ||
        []
    };

    const response = yield saveByocAdjustmentPriceRulesApi(
      locationId,
      requestData
    );

    if (response?.status === 200 || response?.status === 201) {
      const savedData =
        response.data?.adjustmentPriceRules ||
        response.data?.AdjustmentPriceRules ||
        payload?.adjustmentPriceRules ||
        payload?.AdjustmentPriceRules ||
        payload;
      yield put(SAVE_BYOC_ADJUSTMENT_PRICE_RULES_SUCCESS(savedData));
      if (payload?.successCallback) {
        payload.successCallback();
      }
    } else {
      const errorMessage =
        response?.data?.message || 'Failed to save adjustment price rules';
      yield put(SAVE_BYOC_ADJUSTMENT_PRICE_RULES_ERROR(errorMessage));
    }
  } catch (error) {
    yield put(
      SAVE_BYOC_ADJUSTMENT_PRICE_RULES_ERROR(
        error?.response?.data?.message ||
          'Failed to save adjustment price rules'
      )
    );
  }
}

function* getByocAgreement({payload}) {
  try {
    const locationId = yield select((state) => state.user?.locationId);

    if (!locationId) {
      yield put(GET_BYOC_AGREEMENT_ERROR('Location ID is required'));
      return;
    }

    const {ConnectedPlatforms, SelectedPlatforms, IsGetButtonClicked} = payload;

    const mapPlatformsToBackendFormat = (platforms) => {
      const platformMap = {
        uber: 'UberEats',
        grubhub: 'Grubhub',
        doordash: 'DoorDash'
      };

      return platforms.map((platform) => platformMap[platform] || platform);
    };

    const response = yield getByocSelfDeliveryLinkApi(locationId, {
      selfDeliveryProviderOptions: mapPlatformsToBackendFormat(
        ConnectedPlatforms
      )
    });
    if (response?.data?.data?.selfDeliveryAgreementLink) {
      window.open(response?.data?.data?.selfDeliveryAgreementLink, '_blank');
    }

    // Success - update AgreementInfo
    yield put(GET_BYOC_AGREEMENT_SUCCESS());

    if (payload?.successCallback) {
      payload.successCallback();
    }
  } catch (error) {
    yield put(
      GET_BYOC_AGREEMENT_ERROR(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to get agreement'
      )
    );
  }
}

function* updateByocOnboardingStep({payload}) {
  try {
    const locationId = yield select((state) => state.user?.locationId);

    if (!locationId) {
      yield put(UPDATE_BYOC_ONBOARDING_STEP_ERROR('Location ID is required'));
      return;
    }

    const response = yield updateByocOnboardingStepApi(locationId, payload);

    if (response?.status === 200 || response?.status === 201) {
      yield put(UPDATE_BYOC_ONBOARDING_STEP_SUCCESS(response.data || payload));
      if (payload?.orderSourceIntegration?.platformCommissions) {
        yield put(
          UPDATE_BYOC_COMMISSION_STATE(
            payload?.orderSourceIntegration?.platformCommissions
          )
        );
      }
      if (payload?.successCallback) {
        payload.successCallback();
      }
    } else {
      const errorMessage =
        response?.data?.message || 'Failed to update onboarding step';
      yield put(UPDATE_BYOC_ONBOARDING_STEP_ERROR(errorMessage));
    }
  } catch (error) {
    yield put(
      UPDATE_BYOC_ONBOARDING_STEP_ERROR(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to update onboarding step'
      )
    );
  }
}

function* saveByocSignature({payload}) {
  try {
    const locationId = yield select((state) => state.user?.locationId);

    if (!locationId) {
      yield put(SAVE_BYOC_SIGNATURE_ERROR('Location ID is required'));
      return;
    }

    const signature = payload?.signature;
    if (!signature) {
      yield put(SAVE_BYOC_SIGNATURE_ERROR('Signature is required'));
      return;
    }

    // Extract base64 part from data URL if present
    let signatureBase64 = signature;
    if (signatureBase64 && signatureBase64.includes(',')) {
      signatureBase64 = signatureBase64.split(',')[1];
    }

    const response = yield saveByocSignatureApi(locationId, signatureBase64);

    if (response?.status === 200 || response?.status === 201) {
      yield put(SAVE_BYOC_SIGNATURE_SUCCESS({signature: signatureBase64}));
      if (payload?.successCallback) {
        payload.successCallback();
      }
    } else {
      const errorMessage =
        response?.data?.message || 'Failed to save signature';
      yield put(SAVE_BYOC_SIGNATURE_ERROR(errorMessage));
    }
  } catch (error) {
    yield put(
      SAVE_BYOC_SIGNATURE_ERROR(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to save signature'
      )
    );
  }
}

function* byocOnboardingSaga() {
  yield takeEvery(GET_BYOC_ONBOARDING_DATA, getByocOnboardingData);
  yield takeEvery(SAVE_BYOC_STRIPE_FLOW_COMPLETED, saveByocStripeFlowCompleted);
  yield takeEvery(
    SAVE_BYOC_ADJUSTMENT_PRICE_RULES,
    saveByocAdjustmentPriceRules
  );
  yield takeEvery(GET_BYOC_AGREEMENT, getByocAgreement);
  yield takeEvery(UPDATE_BYOC_ONBOARDING_STEP, updateByocOnboardingStep);
  yield takeEvery(SAVE_BYOC_SIGNATURE, saveByocSignature);
}

export default byocOnboardingSaga;
